import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * Custom hook to dynamically update document title for SPA
 * Keeps "Bibliotheca" branding with descriptive page titles
 */
export const useDocumentTitle = (pageTitle?: string) => {
  const { t } = useTranslation();
  
  useEffect(() => {
    // Base title with branding
    const baseTitle = 'Bibliotheca';
    
    // If no page title provided, use just the base
    if (!pageTitle) {
      document.title = baseTitle;
      return;
    }
    
    // Translate the page title if it's a translation key
    const translatedTitle = pageTitle.startsWith('page.') ? t(pageTitle) : pageTitle;
    
    // Set title with format: "Page Title | Bibliotheca"
    document.title = `${translatedTitle} | ${baseTitle}`;
    
    // Optional: Log for debugging
    if (process.env.NODE_ENV === 'development') {
      console.log(`📄 Document title updated to: ${document.title}`);
    }
  }, [pageTitle, t]);
};

/**
 * Set document title directly without hook
 * Useful for one-off title updates
 */
export const setDocumentTitle = (pageTitle: string, useTranslation?: boolean) => {
  const baseTitle = 'Bibliotheca';
  
  if (!pageTitle) {
    document.title = baseTitle;
    return;
  }
  
  // Set title with format: "Page Title | Bibliotheca"
  document.title = `${pageTitle} | ${baseTitle}`;
};

export default useDocumentTitle;
